import { Drawer, Typography, Collapse, Steps, Table, Tag, Alert, Space, Divider } from 'antd';

const { Title, Paragraph, Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
}

/** 角色表（v2：ECN 驱动，无三方审批） */
const roleRows = [
  {
    key: 'pa',
    role: '平台管理员',
    who: '系统运营',
    can: '跨产品；建用户、建产品、加成员（本身不维护 BOM 数据）',
  },
  {
    key: 'admin',
    role: '管理员',
    who: '产品负责人',
    can: '产品内全管理；配置本产品发件邮箱；上传/删除 BOM、ECN、规格书；管理成员/联系人',
  },
  {
    key: 'editor',
    role: '编辑',
    who: '数据维护人',
    can: '上传 BOM（整表覆盖）、上传 ECN 并发送通知、上传规格书；维护通知联系人',
  },
  {
    key: 'viewer',
    role: '审核员',
    who: '查看人',
    can: '查看 BOM、ECN 变更明细、料号变更时间线、规格书（只读）',
  },
];

const featureRows = [
  {
    key: 'f1',
    f: 'BOM 清单',
    where: '左侧「BOM 清单」',
    d: 'Excel 整表上传，不分层级；重新上传即整表覆盖并自动留旧版快照；可导出',
  },
  {
    key: 'f2',
    f: '料号变更时间线',
    where: 'BOM 详情 → 点「料号」',
    d: '抽屉内切换「ECN 变更 / 规格书更新」：ECN 显示变更前后对照并可下载 ECN 存档；规格书显示历次版本并可下载',
  },
  {
    key: 'f3',
    f: 'ECN 变更单',
    where: 'BOM 清单页 →「ECN 变更」标签',
    d: '上传 ECN .xls，自动解析「日期/变更料号/变更前后明细」，原文件留存可下载；支持批量删除',
  },
  {
    key: 'f3b',
    f: '规格书',
    where: 'BOM 清单页 →「规格书」标签',
    d: '按料号归档文件（PDF/Word/Excel/PPT/图片等），多次上传即更新时间线；可下载/删除；不发邮件',
  },
  {
    key: 'f4',
    f: '发件配置',
    where: '左侧「发件配置」（管理员）',
    d: '配置本产品 SMTP；配过的邮箱进「我的发件邮箱」可在其它产品直接选用',
  },
  {
    key: 'f5',
    f: '成员/联系人',
    where: '左侧「成员/联系人」',
    d: '产品成员（下拉选人 + 角色）+ 通知联系人（收件邮箱）；均支持 Excel 导入',
  },
  {
    key: 'f6',
    f: '联系人分组',
    where: '左侧「联系人分组」',
    d: '按公司维护的共享联系人库，可整表导入；产品可从分组一键导入联系人',
  },
  {
    key: 'f7',
    f: '操作日志',
    where: '左侧「操作日志」（管理员）',
    d: '全部操作时间线；点「操作人/对象」即筛选，无需输入 ID；可导出',
  },
];

export function HelpDrawer({ open, onClose }: Props) {
  return (
    <Drawer title="使用教程" open={open} onClose={onClose} width={760}>
      <Paragraph type="secondary">
        BOM 协作（ECN 驱动）：<Tag>管理员</Tag>
        <Tag>编辑</Tag>
        <Tag>审核员</Tag> + 平台管理员。BOM 用 Excel 整表上传，变更以 ECN
        记录并按料号汇成时间线，通知由各产品自己的发件邮箱发出（无三方审批）。
      </Paragraph>

      <Collapse
        defaultActiveKey={['roles', 'flow']}
        items={[
          {
            key: 'roles',
            label: '一、角色与权限（先理解）',
            children: (
              <>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={roleRows}
                  columns={[
                    { title: '角色', dataIndex: 'role', width: 110 },
                    { title: '是谁', dataIndex: 'who', width: 100 },
                    { title: '能做什么', dataIndex: 'can' },
                  ]}
                />
                <Paragraph style={{ marginTop: 8 }} type="secondary">
                  产品 =
                  一个产品/项目，数据按产品隔离。加成员仅管理员/平台管理员；联系人编辑也可维护；审核员只读。
                </Paragraph>
              </>
            ),
          },
          {
            key: 'setup',
            label: '二、第一次设置（平台管理员 / 负责人）',
            children: (
              <Steps
                direction="vertical"
                size="small"
                current={-1}
                items={[
                  {
                    title: '建用户',
                    description:
                      '「用户管理 → 新建用户」：邮箱/姓名/初始密码（≥8 位）。可用「下载模板 + 导入用户」批量建。',
                  },
                  {
                    title: '建产品',
                    description:
                      '「产品管理 → 新建产品」：编码/名称/生产工厂（原客户公司，可选，可从工厂池下拉或输入新的）；可同时选成员与联系人分组。也支持 Excel 批量导入（模板含 成员邮箱/成员角色，可一次建产品并加成员），并可导出产品+成员。',
                  },
                  {
                    title: '加成员、分配角色',
                    description:
                      '顶部「切换产品」进入 →「成员/联系人 → 产品成员 → 添加成员」：从下拉选已有账号（搜姓名/邮箱）+ 角色，无需输入 ID。也可 Excel 导入（邮箱/角色）。',
                  },
                  {
                    title: '配置发件邮箱（发通知的前提）',
                    description:
                      '负责人进「发件配置」填本产品 SMTP（发件邮箱 + 主机/端口 + 密码或授权码）。配过的邮箱可在其它产品「我的发件邮箱」直接选用。未配置则通知不发送。',
                  },
                  {
                    title: '添加通知联系人',
                    description:
                      '「成员/联系人 → 通知联系人」添加收件人，或「从分组导入」/ Excel 导入。进产品「概览」页会引导你完成 SMTP 与联系人两项配置。',
                  },
                ]}
              />
            ),
          },
          {
            key: 'flow',
            label: '三、日常使用流程',
            children: (
              <>
                <Title level={5}>A. 上传 / 更新 BOM</Title>
                <Steps
                  direction="vertical"
                  size="small"
                  current={-1}
                  items={[
                    {
                      title: '上传 BOM',
                      description:
                        '「BOM 清单 → 上传 BOM」：拖入 .xls/.xlsx（需含「子件编号」列）→ 名称/编码自动从成品行（层级 0）带出、可改 → 预览 → 创建并导入。原文件一并存档。',
                    },
                    {
                      title: '整表覆盖更新',
                      description:
                        'BOM 详情「重新上传（覆盖）」→ 拖入新表。旧表自动留存为快照，不丢历史。一个产品只有一份 BOM，后续都在这份上更新。',
                    },
                    {
                      title: '看料号变更 / 规格书',
                      description:
                        '在 BOM 详情点任一「料号」→ 弹出抽屉，可切换「ECN 变更 / 规格书更新」：ECN 视图看变更前后对照并可「下载 ECN 存档」，规格书视图看历次版本并可下载。',
                    },
                  ]}
                />
                <Divider />
                <Title level={5}>B. 上传 ECN 变更单</Title>
                <Paragraph type="secondary">
                  在 <Text strong>BOM 清单页的「ECN 变更」标签</Text>（ECN 已并入 BOM 页）→「上传
                  ECN」→ 拖入 ECN .xls → 自动解析日期、变更料号、每条「变更前 → 变更后」→
                  勾选「发布后通知联系人（附原文件）」即由本产品发件邮箱发出。列表「变更项目」显示「变更前规格
                  → 变更后规格」；支持多选批量删除，原文件随时可下载。
                </Paragraph>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="兼容两种 ECN 表格式：横向（变更前块｜变更后块 并排）与纵向（每 NO. 两行标 前/后，如豪泰 I9 单）；日期支持 YYYY年M月D日 / YYYYMMDD / Excel 日期格。"
                />
                <Alert
                  type="success"
                  showIcon
                  message="ECN 不需手动编号：系统自动生成，并按料号汇入「料号变更时间线」。通知只发给「通知联系人」，不发产品成员。"
                />
                <Divider />
                <Title level={5}>C. 上传 / 归档规格书</Title>
                <Paragraph type="secondary">
                  在 <Text strong>BOM 清单页的「规格书」标签</Text> →「上传规格书」→ 搜索选择
                  <Text strong>归属料号</Text>（只能从本产品 BOM 料号中选择，不能填写 BOM 外料号；
                  <Text strong>可多选</Text>
                  ，多个料号共用同一份规格书）+ 拖入文件（PDF/Word/Excel/PPT/图片/zip/txt/csv）+
                  可填「版本说明」→「上传归档」。列表可下载，管理员可删除。
                </Paragraph>
                <Alert
                  type="warning"
                  showIcon
                  message="规格书按料号跨产品共享：在 A 产品上传后，B 产品的 BOM 含同一料号时也会直接显示（「来源产品」列标注出处）；删除对该规格书的所有归属料号、所有产品生效。规格书仅归档、不发送邮件；同一料号多次上传即形成更新时间线，也可在「料号变更时间线」抽屉切到「规格书更新」查看下载。"
                />
              </>
            ),
          },
          {
            key: 'email',
            label: '四、发件配置（谁负责谁发送）',
            children: (
              <>
                <Paragraph>
                  每个产品独立配置一个发件邮箱（如腾讯企业邮 / 163 / QQ）。认证方式可选
                  <Text strong>「密码」或「授权码」</Text>——多数企业邮/163/QQ 需在邮箱后台开启 SMTP
                  并使用授权码（应用专用密码），不是登录密码。
                </Paragraph>
                <Paragraph type="secondary">
                  密码/授权码加密存储、不回显；保存后可「发送测试邮件」自检，也可「一键清除」配置。
                </Paragraph>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="我的发件邮箱：你配过的邮箱会自动收录到本人名下，在其它产品的发件配置页可直接选用（含密码，免重填），仅对你本人可见。"
                />
                <Alert
                  type="info"
                  showIcon
                  message="发通知时：可自定义主题/正文（留空用默认，主题为「项目名称-BOM 上传：…」/「项目名称-ECN 变更：…」）；只发「通知联系人」；自动排除发件邮箱本身，并密送（BCC）发件人一份留底到其收件箱。"
                />
              </>
            ),
          },
          {
            key: 'groups',
            label: '五、联系人分组（跨产品复用）',
            children: (
              <Paragraph type="secondary">
                左侧「联系人分组」按公司维护共享联系人库。列表页「下载模板 + 导入分组」可用一张表
                （分组名/公司/邮箱/姓名/角色标签/备注）批量建组并加人；进某分组还能单独增删或就该组导入。
                产品侧在「通知联系人 →
                从分组导入」把整组联系人复制进产品（按邮箱去重）。分组内改动可选「同步」到已导入的产品。
              </Paragraph>
            ),
          },
          {
            key: 'audit',
            label: '六、操作日志（审计）',
            children: (
              <Paragraph type="secondary">
                左侧「操作日志」（管理员）记录
                BOM/ECN、规格书、成员、联系人、发件配置、产品、用户的增删改。
                进入即展示全部时间线；用「按操作人筛选」下拉，或直接
                <Text strong>点击表格中的「操作人」「对象」</Text>即可筛选，无需输入
                ID；可「导出日志」为 Excel。
              </Paragraph>
            ),
          },
          {
            key: 'excel',
            label: '七、Excel 导入 / 导出',
            children: (
              <Space direction="vertical">
                <Text>
                  各页均有「下载模板」+「导入」；导入逐行校验，失败行会列明原因；导出自动列宽。
                </Text>
                <Text>
                  • <Text strong>用户管理</Text>：导入 邮箱/姓名/初始密码/平台管理员；导出用户列表
                </Text>
                <Text>
                  • <Text strong>产品管理</Text>：导入
                  编码/名称/生产工厂/成员邮箱/成员角色（按编码建/复用产品并加成员）；导出产品+成员
                </Text>
                <Text>
                  • <Text strong>产品成员</Text>：导入 邮箱/角色（按邮箱匹配已有账号）
                </Text>
                <Text>
                  • <Text strong>通知联系人</Text>：导入 邮箱/姓名/单位/角色标签/备注
                </Text>
                <Text>
                  • <Text strong>联系人分组</Text>：导入
                  分组名/公司/邮箱/姓名/角色标签/备注（自动建组）
                </Text>
                <Text>
                  • <Text strong>操作日志</Text>：导出操作时间线
                </Text>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 4 }}
                  message="导入前有预览弹窗，可直接编辑每格（角色等预设值用下拉、其余可改文本）再确认；成员/产品导入时邮箱在用户管理不存在的行会标红。模板表头已标注可选值与必填（角色中英文皆可），导入时括号说明与 * 自动忽略。"
                />
              </Space>
            ),
          },
          {
            key: 'features',
            label: '八、各功能速查',
            children: (
              <Table
                size="small"
                pagination={false}
                dataSource={featureRows}
                columns={[
                  { title: '功能', dataIndex: 'f', width: 120 },
                  { title: '位置', dataIndex: 'where', width: 200 },
                  { title: '说明', dataIndex: 'd' },
                ]}
              />
            ),
          },
          {
            key: 'faq',
            label: '九、常见问题',
            children: (
              <Space direction="vertical">
                <Text>
                  • <Text strong>通知没发出？</Text>{' '}
                  多半是本产品「发件配置」未填/授权码失效，或没有「通知联系人」，先用「发送测试邮件」自检。
                </Text>
                <Text>
                  • <Text strong>重新上传会丢历史吗？</Text> 不会，整表覆盖前会自动保存旧表快照。
                </Text>
                <Text>
                  • <Text strong>加成员要 UUID 吗？</Text> 不需要，改为下拉选择已有账号。
                </Text>
                <Text>
                  • <Text strong>发件邮箱每个产品都要重配？</Text>{' '}
                  不用，配过一次即进「我的发件邮箱」，其它产品可直接选用。
                </Text>
                <Text>
                  • <Text strong>平台管理员看不到业务数据？</Text> 顶部「切换产品」→
                  选任意产品进入即可。
                </Text>
                <Text>
                  • <Text strong>主账号能删吗？</Text> 不能。主账号 admin@cruise-cloud.com
                  受保护，不可删除/停用/取消平台管理员。
                </Text>
                <Text>
                  • <Text strong>发出的通知自己怎么留底？</Text>{' '}
                  无需操作，系统每次自动密送发件人一份到其收件箱。
                </Text>
                <Text>
                  • <Text strong>登录「网络出错」？</Text> 检查网络能否访问 API；中国大陆可能需
                  VPN/干净网络。
                </Text>
              </Space>
            ),
          },
        ]}
      />
    </Drawer>
  );
}
